import { useContext, useEffect, useState } from "react";
import {
  Box, Grid, Typography, Container, Theme,
  Divider, Backdrop, CircularProgress, Stack
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { createStyles, makeStyles } from "@material-ui/core";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Cookies from "universal-cookie";
import axios, { AxiosInstance } from "axios";
import DateVerrouillageRow from "./DateVerrouillageRow";
import { AlertType } from "../../../../types/AlertType";
import { SnackBarAlert } from "../../../element/SnackBarAlert";
import { loggoutUser } from "../../../../redux/actions/logginUser";
import SquareIcon from '@mui/icons-material/Square';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { MediaQueryContext } from "../../../../utils/contextes/MediaQueryContext";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    filterInput: {
      background: "#fff",
      color: "gray",
      borderRadius: 2,
      "& .MuiPickersPopper-root": {
        "& .Mui-selected": {
          backgroundColor: "orange",
        },
        "& .css-innj4t-MuiPickersYear-yearButton.Mui-selected": {
          backgroundColor: "orange",
        },
      },
      "& .css-1lczath-MuiFormLabel-root-MuiInputLabel-root": {
        marginTop: -7,
        color: "white",
      },
      "& .css-innj4t-MuiPickersYear-yearButton.Mui-selected": {
        background: "orange",
      },
    },
  })
);

export interface DateLock {
  id: number;
  annee: number;
  date_verouillage: string;
  id_date_cloture: number;
  date_cloture: string;
}

const urlBaseApi = process.env.REACT_APP_BASE_API_URL as string;

const DateVerrouillage = () => {
  const classes = useStyles();

  const { isDesktopOrLaptop } = useContext(MediaQueryContext);

  const cookies = new Cookies();
  const token: string = cookies.get("jwt");

  const http: AxiosInstance = axios.create({
    baseURL: urlBaseApi,
    // timeout: 1000,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Add global response interceptor
  http.interceptors.response.use(
    (response) => {
      // Modify response data here, if needed
      return response;
    },
    (error) => {
      console.log(error.message);
      if (error.message === "Request failed with status code 401") {
        // Remove the redux store
        dispatch(loggoutUser());

        // Remove token in navigator cookies
        cookies.remove('jwt', { path: '/' });

        navigate("/sign-in");
      }
      return Promise.reject(error);
    }
  );

  const date = new Date();
  const [selectedYear, setSelectedYear] = useState<Dayjs | null>(dayjs(date));
  const [dateSVerrouillages, setDateSVerrouillages] = useState<DateLock[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);

  const [openSnack, setOpenSnack] = useState<boolean>(false);
  const [alertMessage, setAlerteMessage] = useState<string>("");
  const [alertType, setAlertType] = useState<AlertType>("success");

  const retrieveData = async () => {
    const year = selectedYear?.year();
    if (year) {
      setIsLoading(true);
      await http
        .get(`date-verrouillage/list/${year}`)
        .then((res) => {
          setDateSVerrouillages(res.data.dateVerrouillages);
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };
  useEffect(() => {
    retrieveData();
  }, [selectedYear?.year(), refresh]);

  const handleChangeYear = (e: any) => {
    setSelectedYear(dayjs(e));
  };

  const reloadList = () => {
    setRefresh(!refresh);
  };

  const handleCloseAlert = () => {
    setOpenSnack(false);
  };

  const stackAlert: JSX.Element = SnackBarAlert(
    openSnack,
    handleCloseAlert,
    alertType,
    alertMessage
  );

  return (
    <>
      <Container maxWidth="lg">
        <Grid
          container
          spacing={1}
          columns={{ xs: 6, sm: 12, md: 12 }}
        >
          <Grid item xs={6} sm={5} md={3}>
            <Stack direction="row">
              <SquareIcon fontSize="small" sx={{ color: "#FC8404", mr: 1 }} />
              <Typography variant="subtitle2">Date de Verrouillage</Typography>
            </Stack>
          </Grid>
          <Grid item xs={6} sm={7} md={9}>
            <Stack direction="row" sx={{ marginBottom: { xs: 2 } }}>
              <SquareIcon fontSize="small" sx={{ color: "red", mr: 1 }} />
              <Typography variant="subtitle2">Date de Cloture</Typography>
            </Stack>
          </Grid>
        </Grid>

        <Box sx={{ my: 4 }}>
          <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 12 }}>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', sm: 'center', md: 'end' }
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Année selectionnée..."
                  views={["year"]}
                  className={classes.filterInput}
                  value={selectedYear}
                  onChange={handleChangeYear}
                  minDate={dayjs("2022-12-31")}
                  slotProps={{
                    textField: {
                      size: "small",
                      color: "warning",
                      fullWidth: !isDesktopOrLaptop
                    },
                  }}
                />
              </LocalizationProvider>
              <Divider />
            </Grid>
          </Grid>
        </Box>

        {isDesktopOrLaptop ? (
          <Box mb={3}>
            <Grid container columns={{ xs: 3, sm: 6, md: 12 }} spacing={1}>
              {dateSVerrouillages.map((dateVerr, index) => (
                <DateVerrouillageRow
                  key={index}
                  dateVerr={dateVerr}
                  selectedYear={selectedYear}
                  index={index}
                  http={http}
                  setOpenSnack={setOpenSnack}
                  setAlerteMessage={setAlerteMessage}
                  setAlertType={setAlertType}
                  reloadList={reloadList}
                />
              ))}
            </Grid>
          </Box>
        ) : (
          <Box sx={{ marginTop: '15%', justifyContent: 'center' }}>
            <Typography variant="subtitle2">Cette fonctionnalité ne marche pas avec votre appreil</Typography>
          </Box>
        )}
      </Container>

      <div>
        <Backdrop
          sx={{ color: "white", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
      {stackAlert}
    </>
  );
};

export default DateVerrouillage;
